import React, { useCallback, useEffect, useRef, useState } from 'react';

/**
 * H-FE-1 B5 — THE TABLE WHOSE COLUMNS THE READER SIZES (CQA norm, 24/9;
 * `docs/design/mockups/prototipo-handover-v2.html`, `.tbl th .rz`).
 *
 * Every header cell carries a divider: drag it to make the column wider or
 * narrower (never below `minWidth`), double-click it to give the column back to
 * the automatic layout. Long text wraps; short values (ids, dates, chips) are
 * `nowrap` cells. The scroll stays inside the card (`overflow-x-auto`).
 *
 * The width lives on the header cell itself, so a table re-rendered with new
 * rows keeps the widths the reader chose.
 *
 * TABLE-1 — the same divider on the app's other data tables. A table that
 * already has its own header look passes `bare`: the cell then takes only what
 * the divider needs (`relative`) and the caller's `className`, so nothing about
 * the table's appearance changes. The mechanism is the one above, unchanged.
 */

export const TH_CLASS = 'relative whitespace-nowrap text-left text-[10.5px] font-bold tracking-[0.04em] text-slate-500 px-2.5 py-2 border-b border-slate-800';
export const TD_CLASS = 'px-2.5 py-2 border-b border-slate-800 align-top text-slate-300 break-words';
export const TD_NOWRAP = `${TD_CLASS} whitespace-nowrap`;

export function ResizableTh({
  children, className = '', minWidth = 56, resizable = true, bare = false, testId, ...rest
}) {
  const ref = useRef(null);
  const drag = useRef(null);
  const [width, setWidth] = useState(null);
  const [active, setActive] = useState(false);

  const onMove = useCallback((e) => {
    if (!drag.current) return;
    setWidth(Math.max(minWidth, drag.current.w + (e.clientX - drag.current.x)));
  }, [minWidth]);

  const onUp = useCallback(() => {
    drag.current = null;
    setActive(false);
    document.removeEventListener('mousemove', onMove);
    document.removeEventListener('mouseup', onUp);
  }, [onMove]);

  // Unmounting mid-drag must not leave listeners behind.
  useEffect(() => () => {
    document.removeEventListener('mousemove', onMove);
    document.removeEventListener('mouseup', onUp);
  }, [onMove, onUp]);

  const onDown = (e) => {
    e.preventDefault();
    e.stopPropagation();
    const w = ref.current ? ref.current.getBoundingClientRect().width : 0;
    drag.current = { x: e.clientX, w: w || width || minWidth };
    setActive(true);
    document.addEventListener('mousemove', onMove);
    document.addEventListener('mouseup', onUp);
  };

  const style = width ? { width, minWidth: width, maxWidth: width } : undefined;
  return (
    <th ref={ref} className={`${bare ? 'relative' : TH_CLASS} ${className}`} style={style} data-testid={testId} data-width={width || ''} {...rest}>
      {children}
      {resizable ? (
        <span
          role="separator"
          aria-orientation="vertical"
          title="Drag to set the column width · double-click to reset"
          data-testid={testId ? `${testId}-resize` : undefined}
          onMouseDown={onDown}
          onDoubleClick={(e) => { e.stopPropagation(); setWidth(null); }}
          onClick={(e) => e.stopPropagation()}
          className={`absolute right-0 top-1.5 bottom-1.5 w-2 cursor-col-resize select-none border-r-2 ${active
            ? 'border-orange-500' : 'border-slate-700 hover:border-orange-500'}`}
        />
      ) : null}
    </th>
  );
}

/**
 * The table shell: a card-contained horizontal scroll and a table that takes
 * the width its content and the reader give it.
 */
export default function ResizableTable({ children, testId, className = '' }) {
  return (
    <div className="overflow-x-auto" data-testid={testId ? `${testId}-scroll` : undefined}>
      <table className={`w-full border-collapse text-[12.5px] ${className}`} data-testid={testId}>
        {children}
      </table>
    </div>
  );
}
